import { useState, useEffect } from 'react'
import AnimatedNumber from './AnimatedNumber'
import GlowingTrackBar from './GlowingTrackBar'

// ECC-2:2024 Controls data
const ECC_CONTROLS = {
  total: 111,
  domains: [
    { name: 'الحوكمة والأمن القيادي', code: 'GOV', total: 15, completed: 12, color: '#8B5CF6' },
    { name: 'إدارة المخاطر', code: 'RISK', total: 10, completed: 8, color: '#4CAF50' },
    { name: 'الأمن المادي والبيئي', code: 'PHY', total: 12, completed: 9, color: '#F59E0B' },
    { name: 'إدارة الأصول', code: 'AST', total: 8, completed: 6, color: '#448AFF' },
    { name: 'التحكم في الوصول', code: 'ACC', total: 14, completed: 11, color: '#00BFA5' },
    { name: 'أمن العمليات', code: 'OPS', total: 18, completed: 14, color: '#EF4444' },
    { name: 'أمن الاتصالات', code: 'COM', total: 9, completed: 7, color: '#EC4899' },
    { name: 'أمن تطوير النظم', code: 'DEV', total: 7, completed: 5, color: '#06B6D4' },
    { name: 'إدارة الثغرات', code: 'VUL', total: 6, completed: 4, color: '#F97316' },
    { name: 'المرونة واستمرارية الأعمال', code: 'BCP', total: 12, completed: 9, color: '#22C55E' },
  ],
  severity: {
    critical: 3,
    high: 8,
    medium: 15,
    low: 22,
  },
  evidenceStatus: {
    uploaded: 67,
    pending: 31,
    missing: 13,
  },
  complianceScore: 73, // percentage
}

// Mock controls data
const CONTROLS_LIST = [
  { id: 'GOV-01', title: 'سياسة أمن المعلومات المعتمدة', domain: 'GOV', severity: 'high', status: 'completed', evidence: 'uploaded', dueDate: '2026-04-10' },
  { id: 'GOV-02', title: 'تعيين مسؤول أمن المعلومات', domain: 'GOV', severity: 'critical', status: 'in_progress', evidence: 'pending', dueDate: '2026-04-15' },
  { id: 'RISK-01', title: 'تقييم مخاطر أمن المعلومات', domain: 'RISK', severity: 'high', status: 'completed', evidence: 'uploaded', dueDate: '2026-04-08' },
  { id: 'PHY-01', title: 'التحكم في الدخول للمناطق الحرجة', domain: 'PHY', severity: 'medium', status: 'completed', evidence: 'uploaded', dueDate: '2026-04-12' },
  { id: 'ACC-01', title: 'إدارة هويات المستخدمين', domain: 'ACC', severity: 'high', status: 'in_progress', evidence: 'pending', dueDate: '2026-04-18' },
  { id: 'OPS-01', title: 'إجراءات النسخ الاحتياطي', domain: 'OPS', severity: 'critical', status: 'pending', evidence: 'missing', dueDate: '2026-04-20' },
  { id: 'COM-01', title: 'حماية المعلومات المنقولة', domain: 'COM', severity: 'medium', status: 'completed', evidence: 'uploaded', dueDate: '2026-04-05' },
  { id: 'DEV-01', title: 'أمن تطوير التطبيقات', domain: 'DEV', severity: 'high', status: 'in_progress', evidence: 'pending', dueDate: '2026-04-22' },
  { id: 'VUL-01', title: 'إدارة الثغرات الأمنية', domain: 'VUL', severity: 'medium', status: 'pending', evidence: 'missing', dueDate: '2026-04-25' },
  { id: 'BCP-01', title: 'خطة استمرارية الأعمال', domain: 'BCP', severity: 'critical', status: 'in_progress', evidence: 'pending', dueDate: '2026-04-28' },
]

// Mock API function
const fetchECCData = async () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({
        ...ECC_CONTROLS,
        controls: CONTROLS_LIST,
        lastAudit: '2026-04-14',
        nextAudit: '2026-07-14',
        overallStatus: 'compliant',
      })
    }, 500)
  })
}

export default function MuwafaqECCControls() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedDomain, setSelectedDomain] = useState('all')
  const [selectedSeverity, setSelectedSeverity] = useState('all')

  useEffect(() => {
    const loadData = async () => {
      try {
        const eccData = await fetchECCData()
        setData(eccData)
      } catch (error) {
        console.error('Failed to load ECC data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
    const interval = setInterval(loadData, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [])

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#EF4444'
      case 'high': return '#F59E0B'
      case 'medium': return '#3B82F6'
      case 'low': return '#22C55E'
      default: return '#64748B'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#22C55E'
      case 'in_progress': return '#F59E0B'
      case 'pending': return '#EF4444'
      default: return '#64748B'
    }
  }

  const getEvidenceColor = (evidence) => {
    switch (evidence) {
      case 'uploaded': return '#22C55E'
      case 'pending': return '#F59E0B'
      case 'missing': return '#EF4444'
      default: return '#64748B'
    }
  }

  const filteredControls = data?.controls?.filter(control => {
    if (selectedDomain !== 'all' && control.domain !== selectedDomain) return false
    if (selectedSeverity !== 'all' && control.severity !== selectedSeverity) return false
    return true
  }) || []

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-white/[0.04] rounded-lg w-48 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-64 bg-white/[0.04] rounded-[14px] animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">🛡️ Muwafaq ECC-2:2024 Controls</h2>
            <p className="text-sm text-[#94A3B8]">NCA Compliance Platform - Real-time control tracking</p>
          </div>
        </div>
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-8 text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-[#94A3B8]">Loading compliance data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">🛡️ Muwafaq ECC-2:2024 Controls</h2>
          <p className="text-sm text-[#94A3B8]">NCA Compliance Platform - Real-time control tracking</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-white">
              Compliance Score: <span className="text-2xl font-bold" style={{ color: data.complianceScore >= 80 ? '#22C55E' : data.complianceScore >= 60 ? '#F59E0B' : '#EF4444' }}>
                {data.complianceScore}%
              </span>
            </p>
            <p className="text-xs text-[#94A3B8]">Last audit: {data.lastAudit}</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#94A3B8]">Total Controls</span>
            <span className="text-lg font-bold text-white">{data.total}</span>
          </div>
          <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#C084FC]" style={{ width: '100%' }} />
          </div>
          <p className="text-xs text-[#64748B] mt-2">NCA ECC-2:2024 Standard</p>
        </div>

        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#94A3B8]">Completed</span>
            <span className="text-lg font-bold text-white">
              {data.domains.reduce((sum, domain) => sum + domain.completed, 0)}
            </span>
          </div>
          <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-[#22C55E] to-[#86EFAC]" 
              style={{ 
                width: `${(data.domains.reduce((sum, domain) => sum + domain.completed, 0) / data.total) * 100}%` 
              }} 
            />
          </div>
          <p className="text-xs text-[#64748B] mt-2">
            {Math.round((data.domains.reduce((sum, domain) => sum + domain.completed, 0) / data.total) * 100)}% complete
          </p>
        </div>

        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#94A3B8]">Evidence Uploaded</span>
            <span className="text-lg font-bold text-white">{data.evidenceStatus.uploaded}</span>
          </div>
          <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-[#3B82F6] to-[#93C5FD]" 
              style={{ 
                width: `${(data.evidenceStatus.uploaded / data.total) * 100}%` 
              }} 
            />
          </div>
          <p className="text-xs text-[#64748B] mt-2">
            {data.evidenceStatus.pending} pending, {data.evidenceStatus.missing} missing
          </p>
        </div>

        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#94A3B8]">Next Audit</span>
            <span className="text-lg font-bold text-white">{data.nextAudit}</span>
          </div>
          <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#F59E0B] to-[#FCD34D]" style={{ width: '65%' }} />
          </div>
          <p className="text-xs text-[#64748B] mt-2">90 days remaining</p>
        </div>
      </div>

      {/* Domain Progress & Severity Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Domain Progress */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">📊 Domain Progress</h3>
          <div className="space-y-4">
            {(data.domains || []).map((domain) => {
              const progress = Math.round((domain.completed / domain.total) * 100)
              return (
                <div key={domain.code} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: domain.color }} />
                      <span className="text-sm font-medium text-white">{domain.name}</span>
                      <span className="text-xs text-[#64748B]">({domain.code})</span>
                    </div>
                    <span className="text-sm font-bold text-white">{domain.completed}/{domain.total}</span>
                  </div>
                  <GlowingTrackBar
                    progress={progress}
                    color={domain.color}
                    lightColor={`${domain.color}80`}
                    height="h-2"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#64748B]">{progress}% complete</span>
                    <span className="text-xs text-[#64748B]">{domain.total - domain.completed} remaining</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Severity Distribution */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">⚠️ Severity Distribution</h3>
          <div className="space-y-4">
            {Object.entries(data.severity || {}).map(([severity, count]) => (
              <div key={severity} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSeverityColor(severity) }} />
                    <span className="text-sm font-medium text-white capitalize">{severity}</span>
                  </div>
                  <span className="text-lg font-bold text-white">{count}</span>
                </div>
                <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full" 
                    style={{ 
                      width: `${(count / Object.values(data.severity).reduce((a, b) => a + b, 0)) * 100}%`,
                      backgroundColor: getSeverityColor(severity)
                    }} 
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Evidence Status */}
          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <h4 className="text-md font-bold text-white mb-3">📎 Evidence Status</h4>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(data.evidenceStatus || {}).map(([status, count]) => (
                <div key={status} className="text-center">
                  <div className="text-2xl font-bold mb-1" style={{ color: getEvidenceColor(status) }}>
                    {count}
                  </div>
                  <div className="text-xs text-[#94A3B8] capitalize">{status}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Controls List with Filters */}
      <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white">📋 Controls List</h3>
          <div className="flex items-center gap-3">
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              className="bg-base border border-white/[0.06] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]"
            >
              <option value="all">All Domains</option>
              {(data.domains || []).map(domain => (
                <option key={domain.code} value={domain.code}>{domain.name}</option>
              ))}
            </select>
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className="bg-base border border-white/[0.06] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
        
        {/* Controls Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Control ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Title</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Domain</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Severity</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Evidence</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#94A3B8]">Due Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredControls.map((control) => (
                <tr key={control.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="py-3 px-4">
                    <span className="text-sm font-mono font-bold text-white">{control.id}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-white">{control.title}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs px-2 py-1 rounded-full bg-white/[0.04] text-[#94A3B8]">
                      {control.domain}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="text-xs px-2 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: `${getSeverityColor(control.severity)}20`,
                        color: getSeverityColor(control.severity),
                        border: `1px solid ${getSeverityColor(control.severity)}40`
                      }}
                    >
                      {control.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="text-xs px-2 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: `${getStatusColor(control.status)}20`,
                        color: getStatusColor(control.status),
                        border: `1px solid ${getStatusColor(control.status)}40`
                      }}
                    >
                      {control.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="text-xs px-2 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: `${getEvidenceColor(control.evidence)}20`,
                        color: getEvidenceColor(control.evidence),
                        border: `1px solid ${getEvidenceColor(control.evidence)}40`
                      }}
                    >
                      {control.evidence}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-[#94A3B8]">{control.dueDate}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredControls.length === 0 && (
          <div className="text-center py-8">
            <p className="text-[#94A3B8]">No controls match the selected filters</p>
          </div>
        )}
      </div>
    </div>
  )
}